import type { ReactNode } from 'react'
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core'
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { REPUTATION_INBOX_ENABLED } from '@/lib/reputationInbox'
import { SECTION_META, type StorefrontSection } from './storefrontConfig'

interface Props {
  sections: StorefrontSection[]
  onChange: (next: StorefrontSection[]) => void
  announcementText: string
  announcementUrl: string
  onAnnouncementText: (v: string) => void
  onAnnouncementUrl: (v: string) => void
  about: string
  onAbout: (v: string) => void
  imageCount: number
}

/**
 * The page, top to bottom. The announcement bar and hero sit above this list
 * (the bar is a toggle, the hero a style); everything below the hero is a
 * section the tenant can reorder and switch. Menu is always on.
 */
export function SectionsEditor({
  sections,
  onChange,
  announcementText,
  announcementUrl,
  onAnnouncementText,
  onAnnouncementUrl,
  about,
  onAbout,
  imageCount,
}: Props) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e
    if (!over || active.id === over.id) return
    const from = sections.findIndex((s) => s.id === active.id)
    const to = sections.findIndex((s) => s.id === over.id)
    if (from < 0 || to < 0) return
    onChange(arrayMove(sections, from, to))
  }

  // The Reviews section publishes reviews pulled by the Reputation inbox, so
  // its row is hidden with it (lib/reputationInbox.ts). Only the row goes:
  // the section stays in `sections`, keeping its saved state and position.
  const visible = sections.filter((s) => s.id !== 'reviews' || REPUTATION_INBOX_ENABLED)

  const setEnabled = (id: string, enabled: boolean) =>
    onChange(sections.map((s) => (s.id === id ? { ...s, enabled: s.id === 'menu' ? true : enabled } : s)))

  return (
    <div className="space-y-4">
      <div className="space-y-2 rounded-md border p-3">
        <div className="flex items-baseline justify-between">
          <Label htmlFor="sf-announce">Announcement bar</Label>
          <span className="text-[11px] tabular-nums text-muted-foreground">{announcementText.length}/120</span>
        </div>
        <Input
          id="sf-announce"
          maxLength={120}
          placeholder="Eid hours: open till 2am all week"
          value={announcementText}
          onChange={(e) => onAnnouncementText(e.target.value)}
        />
        <Input
          type="url"
          inputMode="url"
          placeholder="Link (optional), e.g. https://instagram.com/yourcafe"
          value={announcementUrl}
          onChange={(e) => onAnnouncementUrl(e.target.value)}
        />
        <p className="text-[11px] text-muted-foreground">A thin strip above everything else. Leave blank to hide it.</p>
      </div>

      <div className="space-y-2">
        <Label>Sections</Label>
        <p className="text-[11px] text-muted-foreground">Drag to reorder. The hero always comes first.</p>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={visible.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {visible.map((s) => (
                <SortableRow key={s.id} section={s} onEnabled={(v) => setEnabled(s.id, v)}>
                  {s.id === 'about' && s.enabled && (
                    <textarea
                      id="sf-about"
                      rows={3}
                      maxLength={2000}
                      placeholder="Tell customers who you are."
                      value={about}
                      onChange={(e) => onAbout(e.target.value)}
                      className="mt-2 flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    />
                  )}
                  {s.id === 'gallery' && s.enabled && imageCount === 0 && (
                    <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-400">Add images to the library or this section stays empty.</p>
                  )}
                </SortableRow>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  )
}

function SortableRow({
  section,
  onEnabled,
  children,
}: {
  section: StorefrontSection
  onEnabled: (v: boolean) => void
  children?: ReactNode
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id })
  const style = { transform: CSS.Transform.toString(transform), transition }
  const meta = SECTION_META[section.id]
  const locked = section.id === 'menu'
  return (
    <div ref={setNodeRef} style={style} className={`rounded-md border bg-card p-3 ${isDragging ? 'opacity-60' : ''}`}>
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="shrink-0 cursor-grab touch-none text-muted-foreground"
          aria-label={`Drag ${meta.label}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{meta.label}</p>
          <p className="text-xs text-muted-foreground">{meta.hint}</p>
        </div>
        <Switch
          checked={locked ? true : section.enabled}
          disabled={locked}
          onCheckedChange={onEnabled}
          aria-label={`Show ${meta.label}`}
        />
      </div>
      {children}
    </div>
  )
}
